import * as m from '@staiter/i18n';
import type { SalesOrder, SalesOrderStatus } from '@staiter/shared';
import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';
import { Plus, Search, X } from 'lucide-react';
import { useMemo } from 'react';
import { z } from 'zod';
import { EmptyState, PageHeader, Table, type TableColumn } from '~/components/data';
import { Button, Input, Select } from '~/components/primitives';
import { StatusBadge, type StatusBadgeStatus } from '~/components/status';
import { fetchSalesOrders } from '~/lib/api/sales-orders';
import { formatDate, formatMoney } from '~/lib/format';
import { canDo, useUserScope } from '~/lib/rbac/use-user-scope';
import { useActiveEntityStore } from '~/store/active-entity';
import shared from './_auth._shared.module.css';

// FE-18: map SalesOrderStatus → visual semantics for StatusBadge.
const SO_STATUS_TO_BADGE: Record<SalesOrderStatus, StatusBadgeStatus> = {
  draft: 'needs_review',
  quotation: 'needs_review',
  confirmed: 'completed_draft',
  in_progress: 'in_progress',
  delivered: 'completed',
  invoiced: 'completed',
  closed: 'completed',
  cancelled: 'failed',
};

/** G2 — Sales Orders list (URL state + pagination). */
const PAGE_SIZE = 50;
const salesOrdersSearchSchema = z.object({
  status: z
    .enum([
      'draft',
      'quotation',
      'confirmed',
      'in_progress',
      'delivered',
      'invoiced',
      'closed',
      'cancelled',
    ])
    .optional()
    .catch(undefined),
  q: z.string().optional(),
  offset: z.coerce.number().int().min(0).optional().catch(undefined),
});
// S12 — без .strict(): неизвестные query-параметры стрипаются, а не роняют app.

export const Route = createFileRoute('/_auth/sales-orders/')({
  staticData: { minMainWidth: 960 },
  validateSearch: salesOrdersSearchSchema,
  component: SalesOrdersPage,
});

const STATUS_OPTIONS: Array<{ value: SalesOrderStatus | ''; label: string }> = [
  // FE-3 fix: empty-value option now reads as «Все статусы» instead of duplicating the column label.
  { value: '', label: m.so_filter_all_statuses() },
  { value: 'draft', label: m.so_status_draft() },
  { value: 'quotation', label: m.so_status_quotation() },
  { value: 'confirmed', label: m.so_status_confirmed() },
  { value: 'in_progress', label: m.so_status_in_progress() },
  { value: 'delivered', label: m.so_status_delivered() },
  { value: 'invoiced', label: m.so_status_invoiced() },
  { value: 'closed', label: m.so_status_closed() },
  { value: 'cancelled', label: m.so_status_cancelled() },
];

function SalesOrdersPage(): JSX.Element {
  const navigate = useNavigate();
  const search = useSearch({ from: '/_auth/sales-orders/' });
  const status = search.status ?? '';
  const q = search.q ?? '';
  const offset = search.offset ?? 0;
  const setStatus = (next: SalesOrderStatus | ''): void => {
    navigate({
      to: '/sales-orders',
      search: {
        ...(next ? { status: next } : {}),
        ...(q ? { q } : {}),
      } as never,
    });
  };
  const setQ = (next: string): void => {
    navigate({
      to: '/sales-orders',
      search: {
        ...(status ? { status: status as SalesOrderStatus } : {}),
        ...(next ? { q: next } : {}),
      } as never,
    });
  };
  const setOffset = (next: number): void => {
    navigate({
      to: '/sales-orders',
      search: {
        ...(status ? { status: status as SalesOrderStatus } : {}),
        ...(q ? { q } : {}),
        ...(next > 0 ? { offset: next } : {}),
      } as never,
    });
  };
  const query = useQuery({
    queryKey: ['sales-orders', { status, q, offset }],
    queryFn: () =>
      fetchSalesOrders({
        ...(status ? { status } : {}),
        ...(q ? { q } : {}),
        limit: PAGE_SIZE,
        offset,
      }),
  });
  const rows = query.data?.items ?? [];
  const hasMore = rows.length === PAGE_SIZE;
  // F-sync — колонка «Компания» только в агрегатном охвате (холдинг/«Все компании»).
  const aggregateScope = useActiveEntityStore((s) => s.activeScope) !== 'entity';
  const entities = query.data?.entities ?? {};

  const columns: TableColumn<SalesOrder>[] = useMemo(
    () => [
      {
        id: 'code',
        header: m.so_col_code(),
        // FE-281 Pass 34: nowrap (SO-2026-/0001 wrap на 2 строки в narrow column).
        accessor: (r) => (
          <Link
            to="/sales-orders/$id"
            params={{ id: r.id }}
            className={shared.accentLink}
            style={{ whiteSpace: 'nowrap' }}
          >
            {r.code}
          </Link>
        ),
        sticky: true,
        width: '140px',
      },
      ...(aggregateScope
        ? [
            {
              id: 'entity',
              header: m.project_col_entity(),
              accessor: (r: SalesOrder) => entities[r.entity_id]?.name ?? '—',
              width: '180px',
            } satisfies TableColumn<SalesOrder>,
          ]
        : []),
      {
        id: 'customer',
        header: m.so_col_customer(),
        accessor: (r) => r.counterparty_name ?? r.counterparty_id,
      },
      {
        id: 'status',
        header: m.so_col_status(),
        accessor: (r) => (
          <StatusBadge status={SO_STATUS_TO_BADGE[r.status] ?? 'needs_review'} size="sm" />
        ),
        width: '160px',
      },
      {
        id: 'order_date',
        header: m.so_col_order_date(),
        accessor: (r) => formatDate(r.order_date),
        width: '120px',
      },
      {
        id: 'delivery_date',
        header: m.so_col_delivery_date(),
        accessor: (r) => formatDate(r.delivery_expected_date),
        width: '140px',
      },
      {
        id: 'total',
        header: m.so_col_total(),
        // FE-278 Pass 24: nowrap + min-width prevents «358 000,00 / KZT» wrap
        // на 2 lines в narrow column.
        accessor: (r) => (
          <span style={{ whiteSpace: 'nowrap' }}>{formatMoney(r.total_amount)}</span>
        ),
        width: '180px',
        mono: true,
        align: 'right',
      },
    ],
    [aggregateScope, entities],
  );

  const scope = useUserScope();
  const canCreate = canDo(scope, 'sales_order', 'create');
  return (
    <div>
      <PageHeader
        title={m.so_title_list()}
        actions={
          // F38 (WHS-F38): создание — только роли с правом `sales_order:create`.
          canCreate ? (
            <Button onClick={() => navigate({ to: '/sales-orders/new' })}>
              <Plus size={14} /> {m.so_cta_create()}
            </Button>
          ) : undefined
        }
      />
      <div className={shared.filters}>
        <Select
          label={m.so_col_status()}
          hideLabel
          size="sm"
          value={status}
          placeholder={m.so_col_status()}
          options={STATUS_OPTIONS.map((opt) => ({
            value: opt.value,
            label: opt.label,
          }))}
          onChange={(e) => setStatus(e.target.value as SalesOrderStatus | '')}
        />
        {/* FE-43: leadingIcon Search + trailingIcon X (clear) */}
        <Input
          label={m.project_filter_code_placeholder()}
          hideLabel
          placeholder={m.project_filter_code_placeholder()}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          leadingIcon={<Search size={14} />}
          trailingIcon={
            q ? (
              <button
                type="button"
                onClick={() => setQ('')}
                aria-label={m.common_button_cancel()}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 0,
                  color: 'inherit',
                  display: 'inline-flex',
                }}
              >
                <X size={14} />
              </button>
            ) : undefined
          }
        />
      </div>
      {rows.length === 0 && !query.isLoading ? (
        <EmptyState title={m.so_title_list()} description={m.so_cta_create()} />
      ) : (
        <>
          <Table
            data={rows}
            columns={columns}
            loading={query.isLoading}
            rowKey={(r) => r.id}
            ariaLabel={m.so_title_list()}
            stickyFirstColumn
            onRowClick={(r) => navigate({ to: '/sales-orders/$id', params: { id: r.id } })}
          />
          {(offset > 0 || hasMore) && (
            <div style={{ display: 'flex', gap: 8, marginTop: 12, justifyContent: 'flex-end' }}>
              <Button
                size="sm"
                variant="secondary"
                disabled={offset === 0}
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              >
                ←
              </Button>
              <span style={{ alignSelf: 'center', fontSize: 12, opacity: 0.7 }}>
                {offset + 1}–{offset + rows.length}
              </span>
              <Button
                size="sm"
                variant="secondary"
                disabled={!hasMore}
                onClick={() => setOffset(offset + PAGE_SIZE)}
              >
                →
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
